/**
 * referrer-capture.js
 *
 * Cross-affiliate booking-source attribution for ADV's Odysseus pages (ported from ADC).
 *
 * THE PROBLEM
 * Paid ads on external partner sites land users on an Odysseus
 * affiliate subdomain with `?referrer=<source>&saveReferrer=30`. Odysseus stores
 * that source in its own `ody_referrer` cookie, but SUBDOMAIN-scoped (Domain =
 * deals.americandiscountvacations.com, no leading dot). So when the user crosses to
 * another of our affiliates (deals <-> insider <-> globaldeals) via an internal
 * link that carries no referrer param, `ody_referrer` is not readable on the new
 * subdomain, Odysseus sees no source, and the booking is misattributed to the
 * affiliate subdomain instead of the original ad.
 *
 * THE FIX (two parts, both in this file)
 *   Part 1 - Capture the incoming `referrer` (falling back to `utm_source`) into a
 *            PARENT-scoped first-party cookie (`.americandiscountvacations.com`) that
 *            IS readable across all three affiliate subdomains. First-source-wins,
 *            but a genuine new ad source in the URL always overwrites.
 *   Part 2 - A document-level delegated click handler that, at click time,
 *            re-injects `referrer=<source>` (the VALUE ONLY) onto internal
 *            cross-affiliate links, so Odysseus reads the source natively on the
 *            destination subdomain and writes its own `ody_referrer` there. We
 *            never touch `ody_referrer` directly - only the supported URL param.
 *
 * WHY NO saveReferrer ON INTERNAL HOPS
 * The original ad landing URL already carries `saveReferrer=30` (built by the ad
 * platform, we do not touch it). That is what STARTS Odysseus's 30-day attribution
 * window, and the window must count down from that ORIGINAL ad click. Internal
 * cross-affiliate hops therefore carry the referrer VALUE only and deliberately
 * OMIT `saveReferrer`, so a hop lets Odysseus read the source on the destination
 * without re-triggering the save or sliding the 30-day window forward. (Our own
 * `adc_src` parent cookie behaves the same way: it is only (re)written on a genuine
 * arrival - one whose URL carries `saveReferrer` - and never on an internal hop,
 * which carries the referrer VALUE only, so internal hops never reset its expiry
 * either. See PART 1 for why we gate on saveReferrer, not on `referrer`.)
 *
 * WHY referrer ONLY (no utm_* on internal links)
 * Odysseus attributes bookings from the `referrer` param, not from UTMs, so
 * referrer alone fully fixes the attribution problem. Carrying utm_* on INTERNAL
 * links would make GA4 misread each hop as a new campaign/session start
 * (self-referrals, fractured sessions, double-counted campaigns). GA4 cross-domain
 * session continuity is already handled NATIVELY in the GA4 admin (a cross-domain
 * rule "Ends with americandiscountvacations.com" covers all three subdomains) - so
 * this file deliberately contains NO GA4 / `_gl` linker code.
 *
 * PHASE-2 OPTION (documented, NOT implemented here)
 * If we later want line-level cross-affiliate attribution (utm_term encodes the
 * cruise line), the safe approach is to carry it as a NON-UTM custom parameter
 * and/or rely on GA4 referral-exclusion config, verified against live GA4 first -
 * NOT by carrying raw utm_* on internal links. Left here so the option isn't lost.
 *
 * ODYSSEUS INTEGRATION NOTES (see .github/odysseus-integration.md)
 * This file is bundled into the ADV Odysseus bundle (assets/odysseus/index.js) and
 * therefore runs on Odysseus pages
 * AFTER `window.load` has already fired, and the bundle may be evaluated more than
 * once. So: no reliance on `window.load`, document-level event delegation, and
 * idempotency guards on `document`. Capture needs only the URL + cookies, both
 * available immediately, so it does not wait for the DOM.
 */
(function () {
  'use strict';

  // ==========================================================================
  // ACTIVATION / KILL SWITCH
  // ==========================================================================
  // Three-state control so the feature can be canaried on production (the only
  // place real Odysseus pages exist - the staging subdomain does not render
  // Odysseus content) WITHOUT exposing real customers:
  //
  //   1. ENABLED_BY_DEFAULT (below) - the GLOBAL on/off. Ships `false` so the
  //      feature is inert for everyone on deploy. Going live for all customers =
  //      flip this to `true` and redeploy (Odysseus cache rolls over <= ~20 min).
  //      Emergency fleet-wide off is the same lever in reverse (flip + redeploy).
  //   2. Per-browser OPT-IN (isForcedOn) - activates the feature for ONE browser
  //      even while ENABLED_BY_DEFAULT is false. This is how you test against real
  //      production Odysseus pages while customers stay on the inert default.
  //   3. Per-browser OPT-OUT (isDisabled) - disables the feature for ONE browser.
  //      Wins over everything (an explicit off always beats on).
  //
  // isActive() is re-checked on every click (not just at load), so all three can
  // be toggled at runtime; capture re-evaluates on the next page load.

  // GLOBAL on/off. FALSE = inert for all customers. Flip to TRUE + redeploy to go
  // live fleet-wide. This is the primary go-live / emergency-off lever.
  // ADV/newadc canary passed on production 2026-07 (capture-through-consent,
  // cross-subdomain persistence, plain-link cross-affiliate carry) -> enabled.
  const ENABLED_BY_DEFAULT = true;

  // Per-browser OPT-OUT (highest precedence): window.__adcReferrerCaptureDisabled
  // / cookie adc_src_off=1 / localStorage adc_src_off=1.
  function isDisabled () {
    try {
      if (window.__adcReferrerCaptureDisabled === true) {
        return true;
      }
      if (/(?:^|;\s*)adc_src_off=1(?:;|$)/.test(document.cookie)) {
        return true;
      }
      if (window.localStorage && window.localStorage.getItem('adc_src_off') === '1') {
        return true;
      }
    } catch (e) {
      // Storage access can throw in privacy modes - do not force-disable on error.
    }
    return false;
  }

  // Per-browser OPT-IN / canary: window.__adcReferrerCaptureForceOn / cookie
  // adc_src_on=1 / localStorage adc_src_on=1. Activates for this browser only,
  // even while the global default is off.
  function isForcedOn () {
    try {
      if (window.__adcReferrerCaptureForceOn === true) {
        return true;
      }
      if (/(?:^|;\s*)adc_src_on=1(?:;|$)/.test(document.cookie)) {
        return true;
      }
      if (window.localStorage && window.localStorage.getItem('adc_src_on') === '1') {
        return true;
      }
    } catch (e) {
      // Storage access can throw in privacy modes - treat as not forced on.
    }
    return false;
  }

  // The feature runs only if NOT explicitly opted out AND (globally enabled OR this
  // browser opted in). Opt-out always wins.
  function isActive () {
    if (isDisabled()) {
      return false;
    }
    return ENABLED_BY_DEFAULT || isForcedOn();
  }

  // True only if Civic Cookie Control's stored consent has the advertising category
  // accepted. adc_src is an advertising-category cookie, so the on-load capture must
  // not write it before advertising consent (even transiently - Civic would sweep it).
  // Reads the CookieControl cookie the same way cookie-control.js does. Not set yet on
  // the first page of a fresh session; the advertising onAccept re-capture covers that.
  function advertisingConsented () {
    try {
      const m = document.cookie.match(/(?:^|;\s*)CookieControl=([^;]*)/);
      if (!m) {
        return false;
      }
      const cc = JSON.parse(decodeURIComponent(m[1]));
      return !!(cc && cc.optionalCookies && cc.optionalCookies.advertising === 'accepted');
    } catch (e) {
      return false;
    }
  }

  // ==========================================================================
  // CONFIG
  // ==========================================================================
  const COOKIE_NAME = 'adc_src'; // our parent-scoped source cookie (distinct from Odysseus's ody_referrer)
  const COOKIE_DAYS = 30; // our cookie's own lifetime, counted from the original ad click
  // Subdomains that run the Odysseus booking engine, under our registrable domain.
  const AFFILIATE_SUBDOMAINS = ['deals', 'insider', 'globaldeals'];

  // ==========================================================================
  // COOKIE HELPERS
  // Parent-scoped write, cloning the proven parent-cookie pattern already used on
  // the site (e.g. the geoCountryCode cookie set on .americandiscountvacations.com).
  // ==========================================================================

  // Root (parent) domain for our cookie scope = last two labels of the current
  // host, e.g. deals.americandiscountvacations.com -> americandiscountvacations.com.
  // This assumes a simple single-label TLD (`.com`), which holds for every host we
  // run on (*.americandiscountvacations.com here, and *.americandiscountcruises.com
  // for the ADC original). It is NOT a public-suffix-aware registrable-domain
  // calculation and would be wrong for a multi-label suffix like `*.co.uk` - which
  // is fine because we never run on one. Matches the root-domain derivation
  // cookie-control.js uses for its link matching.
  function parentDomain () {
    const parts = window.location.hostname.split('.');
    return parts.length >= 2 ? parts.slice(-2).join('.') : window.location.hostname;
  }

  function writeParentCookie (name, value, days) {
    let expires = '';
    if (days) {
      const d = new Date();
      d.setTime(d.getTime() + (days * 24 * 60 * 60 * 1000));
      expires = '; expires=' + d.toUTCString();
    }
    document.cookie = name + '=' + encodeURIComponent(value) + expires +
      '; domain=.' + parentDomain() + '; path=/; SameSite=Lax; Secure';
  }

  // decodeURIComponent throws a URIError on malformed percent-encoding (e.g. a
  // stray `%` in a tampered cookie or query string). Guard it so a corrupted value
  // can never abort capture or link rewriting - fall back to the raw value.
  function safeDecode (value) {
    try {
      return decodeURIComponent(value);
    } catch (e) {
      return value;
    }
  }

  function readCookie (name) {
    const m = document.cookie.match(new RegExp('(?:^|;\\s*)' + name + '=([^;]*)'));
    return m ? safeDecode(m[1]) : null;
  }

  // ==========================================================================
  // URL HELPERS
  // ==========================================================================

  // Case-INSENSITIVE key lookup. Odysseus's client-side router lowercases the
  // query-string KEYS on its affiliate pages (`saveReferrer` -> `savereferrer`,
  // `sortColumn` -> `sortcolumn`, etc.), so a case-sensitive `.get('saveReferrer')`
  // would miss the arrival gate and capture would never fire on a real Odysseus
  // landing. We match the key case-insensitively but return the VALUE unchanged -
  // Odysseus preserves values, and a source token may legitimately be mixed-case.
  function getParam (search, key) {
    const lowerKey = key.toLowerCase();
    try {
      let found = null;
      new URLSearchParams(search).forEach(function (value, k) {
        if (found === null && k.toLowerCase() === lowerKey) {
          found = value;
        }
      });
      return found;
    } catch (e) {
      const re = new RegExp('[?&]' + key + '=([^&#]*)', 'i');
      const m = re.exec(search);
      return m ? safeDecode(m[1].replace(/\+/g, ' ')) : null;
    }
  }

  // The incoming ad source from the CURRENT url: `referrer` wins; `utm_source` is
  // a fallback for links that carry UTM but no referrer. Blank/absent => null.
  function incomingSource () {
    const ref = getParam(window.location.search, 'referrer');
    if (ref && ref.trim() !== '') {
      return ref.trim();
    }
    const utm = getParam(window.location.search, 'utm_source');
    if (utm && utm.trim() !== '') {
      return utm.trim();
    }
    return null;
  }

  // ==========================================================================
  // PART 1: CAPTURE + PARENT-COOKIE PERSISTENCE
  // ==========================================================================
  // A genuine ad/campaign arrival carries `saveReferrer` (added by the ad platform;
  // our internal cross-affiliate hops append the referrer VALUE ONLY, and
  // cookie-control.js's UTM link decoration adds no saveReferrer either). So the
  // presence of `saveReferrer` is the reliable signal that distinguishes a real
  // arrival from an internal hop.
  //
  // We MUST gate on `saveReferrer`, not on the mere presence of `referrer`: Part 2
  // puts a `referrer=` param on every internal cross-affiliate hop, so gating on
  // `referrer` would make each hop's destination re-write adc_src and slide its
  // 30-day expiry forward - defeating "the window counts from the original click".
  //
  // Precedence (first-source-wins WITH new-source override):
  //   - Arrival (URL has saveReferrer)  -> OVERWRITE adc_src (a real new ad arrival
  //     always wins, even over an existing stored value; its 30-day window starts
  //     here).
  //   - Internal hop (no saveReferrer)  -> DO NOTHING (never clobber the value,
  //     never slide the expiry).
  // Snapshot the arrival at initial script evaluation - whether this page load is a
  // genuine arrival (URL carries `saveReferrer`) and the source it carried - so that
  // neither a later Odysseus SPA route change that rewrites the URL, nor a
  // consent-delayed re-capture (see the consent hook in INIT below), can lose the
  // original arrival's source.
  const ARRIVAL_HAS_SAVE_REFERRER = getParam(window.location.search, 'saveReferrer') !== null;
  const ARRIVAL_SOURCE = incomingSource();

  function captureSource () {
    // Only (re)write on a genuine arrival, never on an internal hop.
    if (!ARRIVAL_HAS_SAVE_REFERRER) {
      return;
    }
    if (ARRIVAL_SOURCE) {
      writeParentCookie(COOKIE_NAME, ARRIVAL_SOURCE, COOKIE_DAYS);
    }
  }

  // ==========================================================================
  // PART 2: CARRY SOURCE ACROSS INTERNAL CROSS-AFFILIATE LINKS
  // ==========================================================================

  // The source to carry on an outbound link: prefer the live URL (freshest), then
  // the stored parent cookie. Null when we have no source at all.
  function storedSource () {
    return incomingSource() || readCookie(COOKIE_NAME);
  }

  // Nearest ancestor <a href> of an event target (handles clicks on inner nodes).
  function findAnchor (node) {
    const start = node && node.nodeType === 3 ? node.parentElement : node;
    return start && start.closest ? start.closest('a[href]') : null;
  }

  // True only for an absolute link to a DIFFERENT one of our affiliate subdomains.
  // Same-affiliate nav is excluded (Odysseus's own ody_referrer already persists
  // within a subdomain); relative links resolve to the current host and are
  // therefore excluded too. Guards against look-alike hosts by requiring an exact
  // `<sub>.<our-registrable-domain>` match.
  function crossAffiliateTarget (url) {
    if (url.protocol !== 'http:' && url.protocol !== 'https:') {
      return false;
    }
    const host = url.hostname.toLowerCase();
    if (host === window.location.hostname.toLowerCase()) {
      return false;
    }
    const root = parentDomain().toLowerCase();
    for (let i = 0; i < AFFILIATE_SUBDOMAINS.length; i++) {
      if (host === AFFILIATE_SUBDOMAINS[i] + '.' + root) {
        return true;
      }
    }
    return false;
  }

  // Append `referrer=<src>` (VALUE ONLY - no saveReferrer, see file header) to a
  // link's href STRING. All three affiliate subdomains share one uniform Odysseus
  // URL format, so this is a single append path with no per-subdomain branching.
  // We append rather than re-serialize via the URL API so an existing query string
  // is preserved byte-for-byte (never re-encode Odysseus's own params). Idempotent:
  // a link that already carries `referrer=` is left untouched (no double-append),
  // so this is safe to run on every mousedown AND click, and safe against Odysseus
  // re-rendering the same link.
  //
  // ?-vs-& robustness: the separator is `?` when the link has no query string yet
  // and `&` otherwise (checked on the pre-hash base), so a paramless cross-affiliate
  // link gets a well-formed `?referrer=...` rather than a malformed `&referrer=...`.
  function appendReferrer (href, src) {
    if (/[?&]referrer=/i.test(href)) {
      return href;
    }
    const add = 'referrer=' + encodeURIComponent(src);
    const hashIdx = href.indexOf('#');
    const hash = hashIdx > -1 ? href.slice(hashIdx) : '';
    const base = hashIdx > -1 ? href.slice(0, hashIdx) : href;
    const sep = base.indexOf('?') > -1 ? '&' : '?';
    return base + sep + add + hash;
  }

  // Capture-phase handler: rewrite the clicked link's href BEFORE navigation and
  // before Odysseus's own handlers run. We never preventDefault/stopPropagation,
  // so Odysseus's click handling and analytics still fire (on the updated href).
  function onLinkActivate (e) {
    if (!isActive()) {
      return;
    }
    const a = findAnchor(e.target);
    if (!a) {
      return;
    }
    const href = a.getAttribute('href');
    if (!href || href.charAt(0) === '#' || href.indexOf('javascript:') === 0 ||
        href.indexOf('mailto:') === 0 || href.indexOf('tel:') === 0) {
      return;
    }
    let dest;
    try {
      dest = new URL(href, window.location.href);
    } catch (err) {
      return;
    }
    if (!crossAffiliateTarget(dest)) {
      return;
    }
    const src = storedSource();
    if (!src) {
      return;
    }
    const updated = appendReferrer(href, src);
    if (updated !== href) {
      a.setAttribute('href', updated);
    }
  }

  // ==========================================================================
  // INIT
  // ==========================================================================

  // Consent-gated capture. adc_src is an advertising-category cookie, so it is never
  // written before advertising consent (not even transiently). Two entry points:
  //   1. window.__adcRunReferrerCapture() - called by cookie-control.js from the
  //      advertising onAccept the moment consent is granted (incl. auto-accept for
  //      unregulated US). Its firing IS the consent signal, so it does NOT re-read the
  //      consent cookie (which can lag onAccept). Uses the arrival snapshot, so it is
  //      unaffected by any URL change since page load.
  //   2. the on-load capture below - runs only if advertising consent is ALREADY
  //      stored (returning/consented visitor). On a fresh session it no-ops; entry (1)
  //      covers the moment consent lands. So adc_src is never written pre-consent.
  window.__adcRunReferrerCapture = function () {
    if (isActive()) {
      captureSource();
    }
  };

  // Guarded so the capture runs at most once per page load even if Odysseus
  // evaluates the bundle more than once. The flag lives on `document`, which is
  // recreated on every full page navigation, so each real page load recaptures.
  // Gated on advertising consent (see above): a fresh session captures via onAccept.
  if (!document.__adcReferrerCaptureRan) {
    document.__adcReferrerCaptureRan = true;
    if (isActive() && advertisingConsented()) {
      captureSource();
    }
  }

  // Register the delegated link handler once. Registered unconditionally (even when
  // currently inactive) because onLinkActivate re-checks isActive() on every event,
  // so activation can be toggled at runtime without a reload. Both events
  // are used: 'mousedown' catches middle-click / ctrl-click new-tab opens (the
  // href is updated before the browser opens it), 'click' catches normal and
  // keyboard activation. appendReferrer is idempotent, so both firing is safe.
  if (!document.__adcReferrerClickAttached) {
    document.__adcReferrerClickAttached = true;
    document.addEventListener('mousedown', onLinkActivate, true);
    document.addEventListener('click', onLinkActivate, true);
  }
})();
